/**
 * KBChatDrawer — "Ask the Library" chatbot.
 *
 * Right-side Sheet that lets a consultant ask natural-language questions
 * across their entire knowledge base (Past Performance, Key Personnel,
 * Certifications, Promoted Pursuits, Winning Proposals). Multi-turn
 * (session_id persisted), rate-limited 30 turns/hour, billed to the
 * consultant's wallet via the v3.14.4 resolver.
 *
 * Requirement #7 — v3.15.0.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../ui/sheet';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Skeleton } from '../ui/skeleton';
import {
  Send,
  Sparkles,
  Bot,
  User,
  Plus,
  History,
  Loader2,
  ExternalLink,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ASSET_TYPE_LABELS = {
  PAST_PERFORMANCE: 'Past Performance',
  KEY_PERSONNEL: 'Key Personnel',
  CERTIFICATION: 'Certifications',
  WINNING_PROPOSAL: 'Winning Proposals',
  PROMOTED_PURSUIT: 'Promoted',
};

const SUGGESTIONS = [
  'Which key personnel have an active TS/SCI clearance?',
  'List past performances over $5M in the last 3 years.',
  'Do we have winning proposals using best value tradeoff?',
  'Which certifications expire in the next 6 months?',
];

export default function KBChatDrawer({ open, onOpenChange }) {
  const { getAuthHeaders } = useAuth();
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [filterTypes, setFilterTypes] = useState([]); // empty = all
  const messagesEndRef = useRef(null);

  // Auto-scroll on new messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const fetchSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/global-kb/chat/sessions`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setSessions(body.sessions || []);
    } catch (e) {
      console.error('KB chat sessions fetch failed', e);
    } finally {
      setSessionsLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (open && historyOpen) {
      fetchSessions();
    }
  }, [open, historyOpen, fetchSessions]);

  const startNewConversation = () => {
    setSessionId(null);
    setMessages([]);
    setInput('');
    setHistoryOpen(false);
  };

  const loadSession = async (id) => {
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/global-kb/chat/sessions/${id}`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const session = await res.json();
      setSessionId(session.id);
      setMessages(session.messages || []);
      setHistoryOpen(false);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load conversation');
    } finally {
      setSending(false);
    }
  };

  const deleteSession = async (id, e) => {
    e.stopPropagation();
    try {
      await fetch(`${API_URL}/api/global-kb/chat/sessions/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      setSessions((s) => s.filter((x) => x.id !== id));
      if (sessionId === id) startNewConversation();
    } catch (err) {
      console.error(err);
    }
  };

  const sendMessage = async (overrideInput) => {
    const question = (overrideInput ?? input).trim();
    if (!question || sending) return;
    setSending(true);

    // Optimistic user bubble
    const optimistic = {
      role: 'user',
      content: question,
      created_at: new Date().toISOString(),
    };
    setMessages((m) => [...m, optimistic]);
    setInput('');

    try {
      const res = await fetch(`${API_URL}/api/global-kb/chat`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question,
          session_id: sessionId,
          asset_types: filterTypes.length ? filterTypes : null,
        }),
      });
      if (res.status === 429) {
        const body = await res.json();
        toast.error(body?.detail?.message || 'Rate limit reached');
        setMessages((m) => m.slice(0, -1));
        setInput(question);
        return;
      }
      if (!res.ok) {
        const errBody = await res.text();
        throw new Error(errBody);
      }
      const body = await res.json();
      setSessionId(body.session_id);
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          content: body.answer,
          citations: body.citations || [],
          retrieved_count: body.retrieved_count,
          created_at: new Date().toISOString(),
        },
      ]);
    } catch (e) {
      console.error('KB chat send failed', e);
      toast.error('Could not send your question. Try again.');
      // Restore the input so user doesn't lose their question
      setMessages((m) => m.slice(0, -1));
      setInput(question);
    } finally {
      setSending(false);
    }
  };

  const toggleFilter = (type) => {
    setFilterTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl p-0 flex flex-col"
        data-testid="kb-chat-drawer"
      >
        <SheetHeader className="px-5 py-4 border-b border-slate-200 bg-gradient-to-r from-purple-50 to-fuchsia-50">
          <div className="flex items-center justify-between gap-2">
            <SheetTitle className="flex items-center gap-2 text-purple-900">
              <Sparkles className="w-5 h-5" />
              Ask the Library
            </SheetTitle>
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setHistoryOpen((v) => !v)}
                data-testid="kb-chat-history-toggle-btn"
              >
                <History className="w-4 h-4 mr-1" />
                History
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={startNewConversation}
                data-testid="kb-chat-new-conversation-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                New
              </Button>
            </div>
          </div>
          <p className="text-xs text-slate-600">
            Ask natural-language questions across your entire knowledge base.
            Answers are grounded in your library — no hallucination.
          </p>
          <SheetDescription className="sr-only">
            Conversational search across the consultant knowledge base. Use the
            text input to ask a question, click a suggestion chip, or open the
            history panel to resume a previous conversation.
          </SheetDescription>
        </SheetHeader>

        {/* History panel */}
        {historyOpen && (
          <div
            className="border-b border-slate-200 bg-slate-50 max-h-60 overflow-y-auto"
            data-testid="kb-chat-history-panel"
          >
            {sessionsLoading ? (
              <div className="p-3 space-y-2">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            ) : sessions.length === 0 ? (
              <div className="p-4 text-sm text-slate-500 text-center">
                No previous conversations yet.
              </div>
            ) : (
              <ul className="divide-y divide-slate-200">
                {sessions.map((s) => (
                  <li
                    key={s.id}
                    className="flex items-center justify-between px-3 py-2 hover:bg-white cursor-pointer"
                    onClick={() => loadSession(s.id)}
                    data-testid={`kb-chat-session-item-${s.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-900 truncate">
                        {s.title || '(untitled conversation)'}
                      </div>
                      <div className="text-[10px] text-slate-500">
                        {s.updated_at
                          ? new Date(s.updated_at).toLocaleString()
                          : ''}
                      </div>
                    </div>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6"
                      onClick={(e) => deleteSession(s.id, e)}
                      data-testid={`kb-chat-session-delete-${s.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5 text-slate-400 hover:text-red-500" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Filter pills */}
        <div className="px-5 py-2 border-b border-slate-100 flex flex-wrap gap-1.5">
          {Object.entries(ASSET_TYPE_LABELS).map(([key, label]) => (
            <Badge
              key={key}
              variant={filterTypes.includes(key) ? 'default' : 'outline'}
              className={`cursor-pointer text-[11px] ${
                filterTypes.includes(key)
                  ? 'bg-purple-600 hover:bg-purple-700'
                  : 'hover:border-purple-300'
              }`}
              onClick={() => toggleFilter(key)}
              data-testid={`kb-chat-filter-${key}`}
            >
              {label}
            </Badge>
          ))}
        </div>

        {/* Message thread */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {messages.length === 0 && !sending && (
            <div className="text-center py-8 space-y-3">
              <Bot className="w-12 h-12 text-purple-300 mx-auto" />
              <p className="text-sm text-slate-600">
                Start by asking a question, or try one of these:
              </p>
              <div className="flex flex-col gap-2 max-w-sm mx-auto">
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(s)}
                    className="text-xs text-left bg-slate-50 hover:bg-purple-50 border border-slate-200 hover:border-purple-300 rounded-md px-3 py-2 text-slate-700"
                    data-testid={`kb-chat-suggestion-${i}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, idx) => (
            <div
              key={idx}
              className={`flex gap-2 ${
                m.role === 'user' ? 'justify-end' : 'justify-start'
              }`}
              data-testid={`kb-chat-message-${idx}`}
            >
              {m.role === 'assistant' && (
                <div className="w-7 h-7 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <Bot className="w-4 h-4 text-purple-700" />
                </div>
              )}
              <div
                className={`max-w-[80%] rounded-lg px-3 py-2 ${
                  m.role === 'user'
                    ? 'bg-purple-600 text-white'
                    : 'bg-slate-100 text-slate-900'
                }`}
              >
                <div className="text-sm whitespace-pre-wrap">{m.content}</div>
                {m.citations && m.citations.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-slate-300 flex flex-wrap gap-1">
                    {m.citations.map((c) => (
                      <Badge
                        key={`${idx}-${c.index}`}
                        variant="outline"
                        className="bg-white text-purple-700 border-purple-200 text-[10px] cursor-pointer"
                        title={c.title}
                        data-testid={`kb-chat-citation-${idx}-${c.index}`}
                      >
                        [{c.index}] {c.title?.slice(0, 30)}
                        {c.title && c.title.length > 30 ? '…' : ''}
                        <ExternalLink className="w-3 h-3 ml-0.5" />
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              {m.role === 'user' && (
                <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-slate-700" />
                </div>
              )}
            </div>
          ))}

          {sending && (
            <div className="flex gap-2 justify-start" data-testid="kb-chat-thinking">
              <div className="w-7 h-7 rounded-full bg-purple-100 flex items-center justify-center">
                <Loader2 className="w-4 h-4 text-purple-700 animate-spin" />
              </div>
              <div className="bg-slate-100 rounded-lg px-3 py-2 text-sm text-slate-500">
                Searching the library…
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="border-t border-slate-200 p-3 bg-white">
          <div className="flex gap-2">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              placeholder="Ask a question about your library…"
              className="min-h-[44px] max-h-[120px] resize-none text-sm"
              disabled={sending}
              data-testid="kb-chat-input"
            />
            <Button
              onClick={() => sendMessage()}
              disabled={sending || !input.trim()}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="kb-chat-send-btn"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-[10px] text-slate-400 mt-1">
            Limited to 30 questions per hour. Answers cite asset indices like
            [1], [2].
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
